import React, { JSX, useEffect, useState } from 'react';
import { CheckIcon, WarningTwoIcon } from '@chakra-ui/icons';
import { Box, Circle, Flex, Progress, Text } from '@chakra-ui/react';
import { Modal, ModalType } from 'common/components/Modal/Modal';
import { useTranslations } from 'contexts/LocalizationContext';

import { ProductResponse } from '../Products/Product.model';
import { SCAN_INFO_MODAL_CLOSE_DELAY, SCAN_INFO_MODAL_UPDATE_INTERVAL } from './utils/assign-product.consts';

type ScanInfoModalProps = {
  isVisible: boolean;
  onClose: () => void;
  product: ProductResponse | null;
  isDuplicate: boolean;
};

const ScanInfoModal = ({ isVisible, onClose, product, isDuplicate }: ScanInfoModalProps): JSX.Element => {
  const translations = useTranslations();
  const [progress, setProgress] = useState(100);

  // Auto-close the modal after 3 seconds with progress bar
  useEffect(() => {
    if (isVisible) {
      // Reset progress when modal becomes visible
      setProgress(100);

      // Set up interval to update progress
      const intervalId = setInterval(() => {
        setProgress((prevProgress) => {
          const newProgress = prevProgress - (100 * SCAN_INFO_MODAL_UPDATE_INTERVAL) / SCAN_INFO_MODAL_CLOSE_DELAY;
          return newProgress > 0 ? newProgress : 0;
        });
      }, SCAN_INFO_MODAL_UPDATE_INTERVAL);

      // Set up timer to close the modal
      const timer = setTimeout(() => {
        onClose();
      }, SCAN_INFO_MODAL_CLOSE_DELAY);

      // Clean up interval and timer on unmount or when modal closes
      return () => {
        clearInterval(intervalId);
        clearTimeout(timer);
      };
    }
  }, [isVisible, onClose]);

  if (!product) {
    return <></>;
  }

  return (
    <Modal
      isModalVisible={isVisible}
      onModalClose={onClose}
      onConfirmClick={onClose}
      type={ModalType.INFO}
      modalHeader={translations('scan_info_modal_header')}
    >
      <Box p={4} position="relative">
        <Flex direction="column" alignItems="center" mb={6}>
          {isDuplicate ? (
            <>
              <Circle size="80px" bg="orange.100" mb={3}>
                <WarningTwoIcon w={10} h={10} color="orange.500" />
              </Circle>
              <Text color="orange.500" fontWeight="bold" fontSize="lg">
                {translations('scan_info_modal_duplicate')}
              </Text>
            </>
          ) : (
            <>
              <Circle size="80px" bg="green.100" mb={3}>
                <CheckIcon w={10} h={10} color="green.500" />
              </Circle>
              <Text color="green.500" fontWeight="bold" fontSize="lg">
                {translations('scan_info_modal_header')}
              </Text>
            </>
          )}
        </Flex>

        <Box mb={4}>
          <Text fontWeight="bold">{translations('scan_info_modal_product_id')}:</Text>
          <Text>{product.id}</Text>
        </Box>
        <Box mb={4}>
          <Text fontWeight="bold">{translations('scan_info_modal_product_name')}:</Text>
          <Text>{product.productName}</Text>
        </Box>
        <Box mb={4}>
          <Text fontWeight="bold">{translations('scan_info_modal_category')}:</Text>
          <Text>{product.category.translations.en}</Text>
        </Box>

        {/* Progress bar for auto-close countdown */}
        <Box position="absolute" bottom="0" left="0" right="0" px={4} pb={2}>
          <Progress
            value={progress}
            size="sm"
            colorScheme={isDuplicate ? 'orange' : 'green'}
            borderRadius="md"
            hasStripe
            isAnimated
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default ScanInfoModal;
